import { enableCachedLoader } from '@gonfalon/dogfood-flags/dogfood-flags';
import { type InternalEnvironment } from '@gonfalon/environments/types';
import { putSelectedEnvironments } from '@gonfalon/openapi/operations/putSelectedEnvironments';
import type { Project } from '@gonfalon/projects/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { isRESTAPIError } from '@gonfalon/rest-api/errors';
import { getEnvironmentsByProjectQuery } from '@gonfalon/rest-api/queries/environments';
import { internalEnvironmentList } from '@gonfalon/rest-api/queries/environments-options';
import { projectsDetail } from '@gonfalon/rest-api/queries/projectsDetail';
import { selectedEnvironments } from '@gonfalon/rest-api/queries/selectedEnvironments';
import { trackError } from '@gonfalon/telemetry/trackError';
import type { QueryClient } from '@tanstack/react-query';

import type { ProjectContext } from '../../projectContext';

export interface ProjectContextForRequest {
  context: ProjectContext;
  project: Project;
  environments: InternalEnvironment[];
  shouldRedirect?: boolean;
}

const fetchDefaultEnvironmentKeys = async (projectKey: string) =>
  new Set(
    (
      await cacheAndFetch(
        getEnvironmentsByProjectQuery({
          projectKey,
          query: { sort: 'name', limit: 2 },
        }),
      )
    ).items
      .slice(0, 2)
      .map((env) => env.key),
  );

const cacheAndFetch: (typeof QueryClient)['prototype']['fetchQuery'] = (options) => {
  const queryClient = getQueryClient();
  const promised = queryClient.fetchQuery(options);
  if (!enableCachedLoader()) {
    return promised;
  }
  const cached = queryClient.ensureQueryData(options);
  return Promise.race([promised, cached]);
};

// This should only be used in `requireProjectContext`
/**
 * Fetches a valid project context for a given request or undefined if none defined in the request.
 *
 * A valid project context includes a project, a set of environments, and a the project context.
 * The projectContext.environmentKeys is the strict set of keys from the array of environments
 * The projectContext.selectedEnvironmentKey is one of projectContext.environmentKeys
 *
 * If no environmentKeys are requested, try pulling it from the last selected environments
 * If there are no API-saved environments, try pulling the first two environments from the project
 * If the selected/requested environment is not in the project, redirect to the first environment
 *
 * @param request - The request object containing the project context.
 * @returns A promise that resolves to an object containing the project context, project details, sorted environments, and a flag indicating if a url redirect is needed.
 * @throws Will throw an error if the project does not have at least two environments.
 */
const getProjectFallback = async (projectKey: string) => {
  const query = projectsDetail({ projectKey });

  try {
    const project = await cacheAndFetch(query);

    return project;
  } catch (e) {
    // If it's a REST API error, throw it, otherwise, it could be a network error, in which case we should try to fallback to the cache
    if (isRESTAPIError(e)) {
      throw e;
    }

    return cacheAndFetch(query);
  }
};

export const NO_AVAILABLE_ENVIRONMENT_ERROR = 'Expected project to have at least two environments';

export async function fetchProjectContextForRequest(
  requestedProjectContext: ProjectContext,
  signal: AbortSignal,
): Promise<ProjectContextForRequest> {
  const projectContext = { ...requestedProjectContext };

  // We don't have any environments in the URL
  if (projectContext.environmentKeys.size === 0) {
    // Ask the API for the last set of environments
    const fromAPI = await cacheAndFetch(selectedEnvironments({ projectKey: projectContext.projectKey }));
    projectContext.environmentKeys = new Set(fromAPI.environments.map((env) => env.key));
    projectContext.selectedEnvironmentKey = fromAPI.selectedEnvironmentKey;

    // The API didn't have any saved environments either
    if (projectContext.environmentKeys.size === 0) {
      // Pick the first two environments from the project by ascending alphabetical order
      projectContext.environmentKeys = await fetchDefaultEnvironmentKeys(projectContext.projectKey);

      // If we still don't have any, we're in trouble
      if (projectContext.environmentKeys.size === 0) {
        throw new Error(NO_AVAILABLE_ENVIRONMENT_ERROR);
      }
    }
  }

  if (
    !projectContext.selectedEnvironmentKey ||
    !projectContext.environmentKeys.has(projectContext.selectedEnvironmentKey)
  ) {
    projectContext.selectedEnvironmentKey = Array.from(projectContext.environmentKeys)[0];
  }

  const [project, environments] = await Promise.all([
    getProjectFallback(projectContext.projectKey),
    cacheAndFetch(
      internalEnvironmentList({
        projectKey: projectContext.projectKey,
        query: {
          key: Array.from(projectContext.environmentKeys).sort(),
        },
      }),
    ),
  ]);

  let sortedEnvironments: InternalEnvironment[] = [];
  for (const key of projectContext.environmentKeys) {
    const environment = environments.items.find((it) => it.key === key);
    if (environment) {
      sortedEnvironments.push(environment);
    } else {
      projectContext.environmentKeys.delete(key);
    }
  }

  // If no environments were fetched, we need to fetch the default environments
  if (sortedEnvironments.length === 0) {
    const defaultEnvironmentKeys = await fetchDefaultEnvironmentKeys(projectContext.projectKey);
    const defaultEnvironments = await cacheAndFetch(
      internalEnvironmentList({
        projectKey: projectContext.projectKey,
        query: {
          key: Array.from(defaultEnvironmentKeys).sort(),
        },
      }),
    );
    sortedEnvironments = defaultEnvironments.items;
    // The user has no access to environments in this project
    if (sortedEnvironments.length === 0) {
      throw new Error(NO_AVAILABLE_ENVIRONMENT_ERROR);
    }
    projectContext.environmentKeys = defaultEnvironmentKeys;
    projectContext.selectedEnvironmentKey = sortedEnvironments[0].key;
  }

  const selectedEnvironmentsQuery = selectedEnvironments({ projectKey: project.key });

  let cacheMissed = false;

  const oldSelectedEnvs = getQueryClient().getQueryData(selectedEnvironmentsQuery.queryKey);

  // Add selected environment if missing from list of environmentKeys
  if (projectContext.selectedEnvironmentKey) {
    projectContext.environmentKeys.add(projectContext.selectedEnvironmentKey);
  }

  if (oldSelectedEnvs) {
    const oldEnvKeys = new Set(oldSelectedEnvs.environments.map((e) => e.key));
    // If `selectedEnvironmentKey` was not updated optimistically, invalidate cache
    if (oldSelectedEnvs.selectedEnvironmentKey !== projectContext.selectedEnvironmentKey) {
      cacheMissed = true;
    }
    // If selected environments changed, invalidate cache
    if (projectContext.environmentKeys.difference(oldEnvKeys).size > 0) {
      cacheMissed = true;
    }
  } else {
    cacheMissed = true;
  }

  if (!signal.aborted && cacheMissed) {
    const syncMessage = {
      projectKey: projectContext.projectKey,
      body: {
        environmentKeys: sortedEnvironments.map((e) => e.key),
        selectedEnvironmentKey: projectContext.selectedEnvironmentKey,
      },
    };
    // TODO: only run this if the cached data does not match the current result
    // running PUTs for cache hits is not necessary.
    await putSelectedEnvironments(syncMessage, { signal })
      .then(() => {
        getQueryClient().setQueryData(selectedEnvironmentsQuery.queryKey, (old) => ({
          ...old,
          environments: sortedEnvironments,
          selectedEnvironmentKey: projectContext.selectedEnvironmentKey,
        }));
      })
      .catch((error) => {
        // Track the error for debugging but don't throw - allow the function to continue
        // The environment selection will still work locally, just not be persisted to the backend
        trackError(
          error instanceof Error
            ? error
            : new Error('Failed to sync selected environments during project context fetch'),
          { severity: 'medium' },
        );
      });
  }

  return {
    context: projectContext,
    project,
    environments: sortedEnvironments,
  };
}
